/** @jsx jsx */
import React, { useState } from 'react';
import { jsx, Container, Grid, Box } from 'theme-ui';
import { useFormik } from 'formik';
import dayjs from 'dayjs';

/* components */
import { Query, SanityEvent } from '../../../../graphql-types';
import Layout from '../../../components/Layout/Layout';
import EventBox from '../../../components/EventBox';
import SEO from '../../../components/SEO';
import JAZZLetters from '../../../components/JAZZLetters';
import translate from '../../../shared/translate';
import { FilterOption, FilterSelect } from './FilterSelect';

const filterByDate = (dates: string[]) => {
  return (event: SanityEvent) => {
    if (!dates || !dates.length) return true;

    return dates.find((date) => {
      const dateToFilterBy = dayjs(date);
      const eventDate = dayjs(event.date.local);

      return dateToFilterBy.isSame(eventDate, 'day');
    });
  };
};

const filterByVenue = (venues: string[]) => {
  return (event: SanityEvent) => {
    if (!venues || !venues.length) return true;
    return venues.find((venue) => event.venue.id === venue);
  };
};

const filterByCategory = (categories: string[]) => {
  return (event: SanityEvent) => {
    if (!categories || !categories.length) return true;
    return categories.find((category) => event?.category?.id === category);
  };
};

type ProgramOverviewType = {
  data: Pick<
    Query,
    'allSanityEvent' | 'allSanityVenue' | 'allSanityEventCategory'
  >;
};
const ProgramOverview: React.FC<ProgramOverviewType> = ({ data }) => {
  const allEvents = data.allSanityEvent.nodes || [];
  const [events, setEvents] = useState<SanityEvent[]>(allEvents);
  const form = useFormik({
    initialValues: {
      program: '',
      date: [],
      venues: [],
      categories: [],
    },
    onSubmit(values) {
      setEvents(
        allEvents
          .filter(filterByDate(values.date))
          .filter(filterByVenue(values.venues))
          .filter(filterByCategory(values.categories))
      );
    },
  });

  const onChange = (eventOrPath: string | React.ChangeEvent<any>) => {
    form.handleChange(eventOrPath);
    setTimeout(form.submitForm, 0);
  };

  const dates = allEvents
    .map((evnt) => dayjs(evnt.date.local).format('YYYY-MM-DD'))
    .filter((val, ind, self) => self.indexOf(val) === ind);

  const venues = data?.allSanityVenue?.nodes;
  const categories = data?.allSanityEventCategory?.nodes;

  const activeCategoriesIds = allEvents
    .filter((event) => !!event.category)
    .map((event) => event.category?.id);

  const activeCategories = categories
    .filter((node) => activeCategoriesIds.indexOf(node.id) > -1)
    .sort((a, b) => (translate(a._rawTitle) < translate(b._rawTitle) ? -1 : 1));
  
  return (
    <Layout>
      <SEO />
      <JAZZLetters />
      <Container sx={{ position: 'relative', p: 2 }}>
        <Grid gap={3} columns={[1, 1, 4]}>
          <Box>
            <Box sx={{ mb: 3 }}>
              <FilterSelect title="Program">
                {activeCategories &&
                  activeCategories.map((category) => (
                    <FilterOption
                      key={category.id}
                      checked={form.values.categories.includes(category.id)}
                      onChange={onChange}
                      name="categories"
                      value={category.id}
                    >
                      {translate(category._rawTitle)}
                    </FilterOption>
                  ))}
              </FilterSelect>
            </Box>
            <Box sx={{ mb: 3 }}>
              <FilterSelect title="Date">
                {dates &&
                  dates.map((date) => (
                    <FilterOption
                      key={date}
                      checked={form.values.date.includes(date)}
                      onChange={onChange}
                      name="date"
                      value={date}
                    >
                      {dayjs(date).format('DD/MM')}
                    </FilterOption>
                  ))}
              </FilterSelect>
            </Box>
            <FilterSelect title="Venue">
              {venues &&
                venues.map((venue) => (
                  <FilterOption
                    key={venue.id}
                    checked={form.values.venues.includes(venue.id)}
                    onChange={onChange}
                    name="venues"
                    value={venue.id}
                  >
                    {translate(venue.title)}
                  </FilterOption>
                ))}
            </FilterSelect>
          </Box>
          {events &&
            events.map((event) => <EventBox key={event.id} {...event} />)}
        </Grid>
      </Container>
    </Layout>
  );
};

export default ProgramOverview;
